import { PRODUCT_CODE_LABEL_MAP } from "./single-invoicing.constants";

export const toApiDate = (date) => {
  if (!date) {
    return null;
  }

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  const year = parsed.getFullYear();
  const month = `${parsed.getMonth() + 1}`.padStart(2, "0");
  const day = `${parsed.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const getSelectedPeriod = ({ invoiceType, invoiceMonth, invoiceYear, invoicePeriod }) => {
  if (invoiceType?.value === "monthly" && invoiceMonth?.value && invoiceYear?.value) {
    const monthIndex = Number(invoiceMonth.value) - 1;
    const year = Number(invoiceYear.value);
    const startDate = new Date(year, monthIndex, 1);
    const endDate = new Date(year, monthIndex + 1, 0);

    return {
      periodStart: toApiDate(startDate),
      periodEnd: toApiDate(endDate),
    };
  }

  if (invoiceType?.value === "custom" && invoicePeriod?.value?.startDate && invoicePeriod?.value?.endDate) {
    return {
      periodStart: toApiDate(invoicePeriod.value.startDate),
      periodEnd: toApiDate(invoicePeriod.value.endDate),
    };
  }

  return {
    periodStart: null,
    periodEnd: null,
  };
};

export const getTransactionDate = (transaction = {}) => {
  return transaction?.date || null;
};

export const getTransactionProduct = (transaction = {}) => {
  const productCode = transaction?.productCode?.toString()?.toLowerCase();
  return PRODUCT_CODE_LABEL_MAP[productCode] || transaction?.productCode || "-";
};

export const getTransactionAmount = (transaction = {}) => {
  const amount = transaction?.finalAmount ?? "";
  const numericAmount = Number(amount);

  if (!Number.isNaN(numericAmount) && Number.isFinite(numericAmount)) {
    return numericAmount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  return `${amount || ""}`;
};

export const getTransactionId = (transaction = {}, index) => {
  return transaction?.transactionId || `tx-${index}`;
};

export const getTransactionCompany = (transaction = {}) => {
  return (
    transaction?.company ||
    transaction?.company_name ||
    transaction?.companyName ||
    transaction?.merchant_company ||
    ""
  );
};

export const toNumber = (value) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getRoundedNumber = (value) => {
  return Number(toNumber(value).toFixed(2));
};

export const formatPeriodDisplay = ({ invoiceType, invoiceMonth, invoiceYear, invoicePeriod }) => {
  if (invoiceType?.value === "monthly" && invoiceMonth?.value && invoiceYear?.value) {
    const monthIndex = Number(invoiceMonth.value) - 1;
    const year = Number(invoiceYear.value);
    const startDate = new Date(year, monthIndex, 1);
    const endDate = new Date(year, monthIndex + 1, 0);

    return `${startDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })} to ${endDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })}`;
  }

  if (invoiceType?.value === "custom" && invoicePeriod?.value?.startDate && invoicePeriod?.value?.endDate) {
    const startDate = new Date(invoicePeriod.value.startDate);
    const endDate = new Date(invoicePeriod.value.endDate);

    return `${startDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })} to ${endDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })}`;
  }

  return "-";
};

export const formatPeriodFromApi = (periodStart, periodEnd) => {
  if (!periodStart || !periodEnd) {
    return "-";
  }

  const startDate = new Date(periodStart);
  const endDate = new Date(periodEnd);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return "-";
  }

  return `${startDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })} to ${endDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })}`;
};

export const isExemptedTransaction = (transaction = {}) => {
  if (typeof transaction?.isReceiptIssued === "boolean") {
    return transaction.isReceiptIssued;
  }

  const exemptedValue =
    transaction?.is_exempted ??
    transaction?.isExempted ??
    transaction?.exempted ??
    transaction?.invoice_exempted ??
    false;

  return exemptedValue === true || exemptedValue === "true" || exemptedValue === 1;
};

export const getTransactionDateLabel = (transaction = {}) => {
  const dateValue = getTransactionDate(transaction);

  if (!dateValue) {
    return "-";
  }

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return "-";
  }

  return parsed.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

export const getTransactionDateShortLabel = (transaction = {}) => {
  const dateValue = getTransactionDate(transaction);

  if (!dateValue) {
    return "-";
  }

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return "-";
  }

  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const getTransactionAmountWithCurrency = (transaction = {}) => {
  return `P${getTransactionAmount(transaction)}`;
};

export const getSearchableLabel = (transaction = {}) => {
  const dateLabel = getTransactionDateLabel(transaction);
  const transactionId = getTransactionId(transaction);
  const product = getTransactionProduct(transaction);
  const amount = getTransactionAmount(transaction);

  return `${dateLabel} ${transactionId} ${product} ${amount}`;
};

export const getTransactionsFromResponse = (res) => {
  if (Array.isArray(res?.transactions)) {
    return res.transactions;
  }

  return [];
};

export const normalizeSelection = (selection = []) => {
  return selection.map(({ value, label }) => ({ value, label }));
};

export const areSelectionsEqual = (a = [], b = []) => {
  if (a.length !== b.length) {
    return false;
  }

  const setA = new Set(a.map((item) => item.value));
  return b.every((item) => setA.has(item.value));
};

export const getChangedSelectionCount = (previous = [], current = []) => {
  const previousSet = new Set(previous.map((item) => item.value));
  const currentSet = new Set(current.map((item) => item.value));
  let changedCount = 0;

  previousSet.forEach((value) => {
    if (!currentSet.has(value)) {
      changedCount += 1;
    }
  });

  currentSet.forEach((value) => {
    if (!previousSet.has(value)) {
      changedCount += 1;
    }
  });

  return changedCount;
};

